import React from 'react';

import { Button as MuiButton, CircularProgress } from '@material-ui/core';
import { ButtonProps as MuiButtonProps } from '@material-ui/core/Button';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  endIcon?: JSX.Element;
  size?: 'small' | 'medium' | 'large';
  startIcon?: JSX.Element;
  status?: 'default' | 'disabled' | 'loading';
  variant?: 'primary' | 'secondary' | 'inverse' | 'text';
}

const useStyles = makeStyles((theme: Theme) => ({
  inverse: {
    background: 'none',
    border: `1px solid ${theme.palette.common.white}`,
    color: theme.palette.common.white,

    '&:hover': {
      background: 'none',
    },
  },

  loadingState: {
    opacity: 0.2,
  },

  loader: {
    left: 'calc(50% - 12)',
    position: 'absolute',
    top: 'calc(50% - 12)',
  },
}));

export const Button = (props: ButtonProps): JSX.Element => {
  const classes = useStyles();
  const {
    children,
    endIcon = '',
    fullWidth = false,
    size = 'medium',
    status = 'default',
    startIcon = '',
    variant = 'primary',
    ...rest
  } = props;

  const getMuiVariant = (fdVariant) => {
    switch (fdVariant) {
      case 'secondary':
        return 'outlined';
      case 'inverse':
        return 'outlined';
      case 'text':
        return 'text';
      case 'primary':
      default:
        return 'contained';
    }
  };

  const buttonStyles = clsx(variant === 'inverse' && classes.inverse);
  const buttonContentStyles = clsx(
    'buttonState',
    status === 'loading' && classes.loadingState,
  );

  return (
    <MuiButton
      className={buttonStyles}
      disabled={status !== 'default'}
      disableRipple={variant === 'text'}
      endIcon={endIcon}
      fullWidth={fullWidth}
      size={size}
      startIcon={startIcon}
      variant={getMuiVariant(variant)}
      {...rest}
    >
      <div className={buttonContentStyles}>{children}</div>

      {status === 'loading' && (
        <CircularProgress
          aria-hidden
          className={classes.loader}
          data-fd="loading"
          size={20}
        />
      )}
    </MuiButton>
  );
};
